import React, { useState, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretUp, faStar, faPenToSquare, faClipboardList, faRecycle, faEye, faTrash } from '@fortawesome/free-solid-svg-icons'
import { Link } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface Plan {
  id         : number
  name       : string
  description: string
  expiry_time: number
  best_value : boolean
  order      : number
  visibility : boolean
  Time       : { name: string }
}

interface Product {
  id           : number
  name         : string
  description  : string
  image        : string
  visibility   : boolean
  list_price   : number
  list_price_2 : number
  list_price_mx: number
  list_price_mx_2: number
  order        : number
  Plans        : Plan[]
}

interface Props {
  product    : Product
  i          : number
  url        : string | undefined
  setProducts: (p: Product[]) => void
}

const pill = (bg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
  padding: '0.25rem 0.6rem', borderRadius: 9999, backgroundColor: bg,
  color: '#fff', fontWeight: 700, fontSize: '0.75rem', border: 'none',
  cursor: 'pointer',
})

const CardProduct: React.FC<Props> = ({ product: e, i, url, setProducts }) => {
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()

  const [loading, setLoading]       = useState(false)
  const [plansTrash, setPlansTrash] = useState<Plan[] | null>(null)
  const [plansOpen, setPlansOpen]   = useState(false)

  const confirmAction = async (title: string, text: string, confirmText: string) => {
    const result = await Swal.fire({
      title,
      text,
      showCancelButton: true,
      confirmButtonText: confirmText,
      customClass: { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary mx-2' },
      buttonsStyling: false,
    })
    return result.isConfirmed
  }

  const softDeleteProduct = async (id: number) => {
    if (!await confirmAction(t('products.archive_product'), t('products.archive_product_txt'), t('products.archive'))) return
    try {
      const headers = await getHeaders()
      const res     = await axios.delete(`${url}products/destroy/${id}`, { headers })
      setProducts(res.data.message)
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const softDeletePlan = async (id: number) => {
    if (!await confirmAction(t('products.archive_plan'), t('products.archive_plan_txt'), t('products.archive'))) return
    try {
      const headers = await getHeaders()
      const res     = await axios.delete(`${url}plans/destroy/${id}`, { headers })
      setProducts(res.data.message)
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const getPlansTrash = async (id: number) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.get(`${url}plans/trash/${id}`, { headers })
      setPlansTrash(res.data.message)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const restorePlan = async (id: number) => {
    if (!await confirmAction(t('products.restore_plan'), t('products.restore_plan_txt'), t('products.restore'))) return
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.put(`${url}plans/restore/${id}`, {}, { headers })
      setProducts(res.data.message)
      setPlansTrash(prev => prev ? prev.filter(x => x.id !== id) : null)
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="row w-100 mt-2">
      <div className="col-12">
        <div style={{ backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden', marginBottom: '1rem' }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div className="row g-0">

            {/* Image */}
            <div className="col-md-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8fafc', minHeight: 140 }}>
              <img
                src={`${url}images/${e.image}`}
                className="img-fluid p-3"
                alt={e.name}
                style={{ maxHeight: 140, objectFit: 'contain' }}
              />
            </div>

            {/* Content */}
            <div className="col-md-9">
              <div style={{ padding: '1rem 1.25rem' }}>

                {/* Header row */}
                <div className="d-flex align-items-start justify-content-between mb-2">
                  <div>
                    <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>{e.name}</p>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>{e.description}</p>
                  </div>
                  <div className="d-flex gap-2">
                    <Link to={`products/edit/${e.id}`} style={pill('#22c55e')} title="Edit">
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Link>
                    <button style={pill('#ef4444')} onClick={() => softDeleteProduct(e.id)} title="Archive">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>

                {/* Details grid */}
                <div className="row g-1" style={{ fontSize: '0.8rem', color: '#475569', marginBottom: '0.75rem' }}>
                  <div className="col-sm-6">
                    <span><b>{t('products.visibility')}:</b> {e.visibility ? t('products.show') : t('products.hidden')}</span>
                  </div>
                  <div className="col-sm-6">
                    <span><b>{t('products.order')}:</b> {e.order}</span>
                  </div>
                  <div className="col-sm-6">
                    <span><b>{t('products.list_price')}:</b> ${e.list_price} usd</span>
                  </div>
                  <div className="col-sm-6">
                    <span><b>{t('products.list_price_2')}:</b> ${e.list_price_2} usd</span>
                  </div>
                  <div className="col-sm-6">
                    <span><b>Precio lista MXN:</b> ${e.list_price_mx} mxn</span>
                  </div>
                  <div className="col-sm-6">
                    <span><b>Precio lista 2 MXN:</b> ${e.list_price_mx_2} mxn</span>
                  </div>
                </div>

                {/* Plans toggle */}
                <button
                  type="button"
                  onClick={() => setPlansOpen(p => !p)}
                  style={{ ...pill(plansOpen ? BRAND_DARK : BRAND), width: '100%', justifyContent: 'center', padding: '0.4rem 1rem', fontSize: '0.82rem' }}
                >
                  <span>{t('products.plans_title')}</span>
                  <FontAwesomeIcon icon={plansOpen ? faCaretUp : faCaretDown} />
                </button>

                {/* Plans panel */}
                {plansOpen && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div className="d-flex justify-content-end mb-2 pb-2 border-bottom">
                      <Link to={`plans/create/${e.id}`} style={pill(BRAND)}>
                        <FontAwesomeIcon icon={faClipboardList} />
                        <span>{t('products.add_plan')}</span>
                      </Link>
                    </div>

                    {/* Active plans */}
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {e.Plans.map((el, il) => (
                        <li key={il} style={{ borderBottom: '1px solid #f1f5f9', padding: '0.6rem 0' }}>
                          <div className="d-flex align-items-center justify-content-between">
                            <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                              <p className="mb-0"><b>{t('products.plan_name')}:</b> {el.name}</p>
                              <p className="mb-0"><b>{t('products.plan_desc')}:</b> {el.description}</p>
                              <p className="mb-0 text-capitalize"><b>{t('products.duration')}:</b> {el.expiry_time} {el.Time.name}</p>
                              <p className="mb-0"><b>{t('products.order')}:</b> {el.order} | <b>{t('products.visibility')}:</b> {el.visibility ? 'Show' : 'Hidden'}</p>
                              {el.best_value
                                ? <p className="mb-0"><b>{t('products.plans.best')}: </b><FontAwesomeIcon icon={faStar} style={{ color: 'gold' }} /></p>
                                : <p className="mb-0 text-muted">{t('products.plans.normal')}</p>
                              }
                            </div>
                            <div className="d-flex gap-2 ms-2">
                              <Link to={`plans/edit/${el.id}`} style={pill('#22c55e')}>
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </Link>
                              <button style={pill('#ef4444')} onClick={() => softDeletePlan(el.id)}>
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>

                    {/* Archived plans section */}
                    <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f1f5f9' }}>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => getPlansTrash(e.id)}
                        className="btn btn-sm btn-warning d-flex align-items-center gap-2"
                        style={{ opacity: 0.7 }}
                      >
                        <FontAwesomeIcon icon={faRecycle} />
                        <span>{t('products.plans_archived_txt')}</span>
                        {loading && <span className="spinner-border spinner-border-sm" role="status" />}
                      </button>

                      {plansTrash && plansTrash.length > 0 && (
                        <ul style={{ listStyle: 'none', padding: 0, marginTop: '0.5rem' }}>
                          {plansTrash.map((em, im) => (
                            <li key={im} style={{ borderBottom: '1px solid #f1f5f9', padding: '0.6rem 0', opacity: 0.65 }}>
                              <div className="d-flex align-items-center justify-content-between">
                                <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                                  <p className="mb-0"><b>{t('products.plan_name')}:</b> {em.name}</p>
                                  <p className="mb-0"><b>{t('products.plan_desc')}:</b> {em.description}</p>
                                  <p className="mb-0 text-capitalize"><b>{t('products.duration')}:</b> {em.expiry_time} {em.Time.name}</p>
                                  {em.best_value
                                    ? <p className="mb-0"><b>{t('products.plans.best')}: </b><FontAwesomeIcon icon={faStar} style={{ color: 'gold' }} /></p>
                                    : <p className="mb-0 text-muted">{t('products.plans.normal')}</p>
                                  }
                                </div>
                                <button
                                  style={pill('#0ea5e9')}
                                  onClick={() => restorePlan(em.id)}
                                  disabled={loading}
                                  title={t('products.restore')}
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}

                      {plansTrash !== null && plansTrash.length === 0 && (
                        <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.5rem' }}>—</p>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CardProduct
